/**
 * The ObjectPool class is designed to manage object pooling,
 * a technique that optimizes resource reuse by recycling objects
 * instead of frequently creating and destroying them.
 */
export class ObjectPool {
  /**
   * This class allows users to define custom object creation logic and manage the pool of objects efficiently.
   * @param {*} createFn A delegate function responsible for creating new objects.
   * @param {*} initialSize The initial number of objects to pre-create and store in the pool. Defaults to 10.
   */
  constructor(createFn, initialSize = 10) {
    if (typeof createFn !== 'function') {
      throw new Error('createFn must be a valid function');
    }

    this.createFn = createFn;
    this.pool = [];

    for (let i = 0; i < initialSize; i++) {
      this.pool.push(this.createFn());
    }
  }

  /**
   * Retrieves an object from the pool. If the pool has available objects, it will return one of them.
   * If the pool is empty, it will create a new object using the createFn function.
   * @returns An object from the pool or a newly created object if the pool is empty.
   */
  acquire() {
    if (this.pool.length > 0) {
      return this.pool.pop();
    } else {
      return this.createFn();
    }
  }

  /**
   * Releases an object back into the pool, making it available for reuse.
   * This method can be used to return objects that are no longer needed,
   * preventing unnecessary creation of new objects.
   * @param {*} object The object to be released back into the pool.
   */
  release(object) {
    this.pool.push(object);
  }

  /**
   * Returns the current number of objects available in the pool.
   * This helps track how many objects are ready for reuse.
   */
  size() {
    return this.pool.length;
  }

  /**
   * Allows dynamic resizing by adding more objects when necessary.
   * @param {*} newSize The target size for the pool.
   */
  resize(newSize) {
    while (newSize > this.pool.length) {
      this.pool.push(this.createFn());
    }
    this.pool.length = newSize;
  }

  /**
   * Randomly reorders the objects in the pool,
   * ensuring a uniform random distribution of the objects.
   */
  shuffle() {
    for (let i = this.pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.pool[i], this.pool[j]] = [this.pool[j], this.pool[i]];
    }
  }
}
